import { supabase } from '../lib/supabase';

export const manualPaymentVerificationService = {
  /**
   * Get all pending manual payment verifications
   */
  async getPendingPaymentVerifications() {
    try {
      const { data, error } = await supabase
        ?.from('payment_transactions')
        ?.select(`
          *,
          company_settings!payment_transactions_company_id_fkey (
            id,
            company_name,
            contact_email,
            contact_phone
          )
        `)
        ?.in('payment_method', ['bsp', 'kinabank', 'creditbank'])
        ?.eq('reconciliation_status', 'pending')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(payment => ({
        id: payment?.id,
        companyId: payment?.company_id,
        companyName: payment?.company_settings?.company_name,
        contactEmail: payment?.company_settings?.contact_email,
        contactPhone: payment?.company_settings?.contact_phone,
        amount: payment?.amount,
        currency: payment?.currency,
        paymentMethod: payment?.payment_method,
        transactionReference: payment?.transaction_reference,
        receiptNumber: payment?.receipt_number,
        reconciliationStatus: payment?.reconciliation_status,
        status: payment?.status,
        createdAt: payment?.created_at,
        updatedAt: payment?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching pending payment verifications:', error);
      throw error;
    }
  },

  /**
   * Verify and reconcile a manual payment
   */
  async verifyPayment(paymentId, verificationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const { data, error } = await supabase
        ?.from('payment_transactions')
        ?.update({
          reconciliation_status: 'reconciled',
          reconciled_at: new Date()?.toISOString(),
          status: 'succeeded',
          transaction_reference: verificationData?.bankReference || null,
          metadata: {
            verified_by: user?.email,
            verified_at: new Date()?.toISOString(),
            verification_notes: verificationData?.notes,
            bank_statement_date: verificationData?.bankStatementDate
          }
        })
        ?.eq('id', paymentId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Log audit trail
      await supabase?.from('workforce_audit_logs')?.insert({
        company_id: data?.company_id,
        action_type: 'billing_updated',
        entity_type: 'payment_transaction',
        entity_id: paymentId,
        performed_by: user?.email,
        changes: {
          reconciliation_status: 'reconciled',
          verified_by: user?.email,
          verification_notes: verificationData?.notes
        }
      });

      return {
        id: data?.id,
        reconciliationStatus: data?.reconciliation_status,
        reconciledAt: data?.reconciled_at
      };
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  },

  /**
   * Get pending account activation requests
   */
  async getPendingActivationRequests() {
    try {
      const { data, error } = await supabase
        ?.from('company_subscriptions')
        ?.select(`
          *,
          company_settings!company_subscriptions_company_id_fkey (
            id,
            company_name,
            company_legal_name,
            contact_email,
            contact_phone,
            is_active
          )
        `)
        ?.in('subscription_status', ['suspended', 'cancelled'])
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(sub => ({
        id: sub?.id,
        companyId: sub?.company_id,
        companyName: sub?.company_settings?.company_name,
        companyLegalName: sub?.company_settings?.company_legal_name,
        contactEmail: sub?.company_settings?.contact_email,
        contactPhone: sub?.company_settings?.contact_phone,
        currentTier: sub?.current_tier,
        subscriptionStatus: sub?.subscription_status,
        activeEmployeeCount: sub?.active_employee_count,
        monthlyPriceKina: sub?.monthly_price_kina,
        nextBillingDate: sub?.next_billing_date,
        isActive: sub?.company_settings?.is_active,
        createdAt: sub?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching pending activation requests:', error);
      throw error;
    }
  },

  /**
   * Activate client account and grant portal access
   */
  async activateClientAccount(activationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { companyId, subscriptionTier, employeeCount, notes } = activationData;

      // Calculate monthly price based on tier
      const tierPricing = {
        basic: 299,
        standard: 599,
        premium: 999,
        enterprise: 1499
      };

      const monthlyPrice = tierPricing?.[subscriptionTier] || 299;

      // Update subscription status
      const { data: subscription, error: subError } = await supabase
        ?.from('company_subscriptions')
        ?.update({
          subscription_status: 'active',
          current_tier: subscriptionTier,
          active_employee_count: employeeCount,
          monthly_price_kina: monthlyPrice,
          billing_period_start: new Date()?.toISOString()?.split('T')?.[0],
          billing_period_end: new Date(new Date()?.setMonth(new Date()?.getMonth() + 1))?.toISOString()?.split('T')?.[0],
          next_billing_date: new Date(new Date()?.setMonth(new Date()?.getMonth() + 1))?.toISOString()?.split('T')?.[0]
        })
        ?.eq('company_id', companyId)
        ?.select()
        ?.single();

      if (subError) throw subError;

      // Activate company settings
      const { error: settingsError } = await supabase
        ?.from('company_settings')
        ?.update({ is_active: true })
        ?.eq('id', companyId);

      if (settingsError) throw settingsError;

      // Log audit trail
      await supabase?.from('workforce_audit_logs')?.insert({
        company_id: companyId,
        action_type: 'billing_updated',
        entity_type: 'company_subscription',
        entity_id: subscription?.id,
        performed_by: user?.email,
        tier_before: null,
        tier_after: subscriptionTier,
        employee_count_after: employeeCount,
        billing_impact_kina: monthlyPrice,
        changes: {
          subscription_status: 'active',
          activation_method: 'manual_admin',
          activation_notes: notes,
          activated_by: user?.email
        }
      });

      return {
        success: true,
        subscription: {
          id: subscription?.id,
          subscriptionStatus: subscription?.subscription_status,
          currentTier: subscription?.current_tier,
          monthlyPriceKina: subscription?.monthly_price_kina
        }
      };
    } catch (error) {
      console.error('Error activating client account:', error);
      throw error;
    }
  },

  /**
   * Get manual override queue (accounts needing admin intervention)
   */
  async getManualOverrideQueue() {
    try {
      // Get companies with payment issues or suspended accounts
      const { data, error } = await supabase
        ?.from('company_subscriptions')
        ?.select(`
          *,
          company_settings!company_subscriptions_company_id_fkey (
            id,
            company_name,
            contact_email,
            is_active
          )
        `)
        ?.or('subscription_status.eq.suspended,subscription_status.eq.cancelled')
        ?.order('updated_at', { ascending: false });

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        companyId: item?.company_id,
        companyName: item?.company_settings?.company_name,
        contactEmail: item?.company_settings?.contact_email,
        subscriptionStatus: item?.subscription_status,
        currentTier: item?.current_tier,
        monthlyPriceKina: item?.monthly_price_kina,
        activeEmployeeCount: item?.active_employee_count,
        nextBillingDate: item?.next_billing_date,
        isActive: item?.company_settings?.is_active,
        priority: item?.subscription_status === 'suspended' ? 'high' : 'medium',
        updatedAt: item?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching manual override queue:', error);
      throw error;
    }
  },

  /**
   * Grant specific portal access permissions
   */
  async grantPortalAccess(accessData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { companyId, userEmail, roleId, department, permissions } = accessData;

      // Assign role to user
      const { data: assignment, error: assignError } = await supabase
        ?.from('user_role_assignments')
        ?.insert({
          user_email: userEmail,
          role_id: roleId,
          department: department,
          assigned_by: user?.email,
          is_active: true
        })
        ?.select()
        ?.single();

      if (assignError) throw assignError;

      // Log audit trail
      await supabase?.from('workforce_audit_logs')?.insert({
        company_id: companyId,
        action_type: 'employee_updated',
        entity_type: 'user_role_assignment',
        entity_id: assignment?.id,
        performed_by: user?.email,
        changes: {
          access_granted: true,
          user_email: userEmail,
          role_id: roleId,
          granted_by: user?.email,
          permissions: permissions
        }
      });

      return {
        success: true,
        assignment: {
          id: assignment?.id,
          userEmail: assignment?.user_email,
          roleId: assignment?.role_id,
          assignedAt: assignment?.assigned_at
        }
      };
    } catch (error) {
      console.error('Error granting portal access:', error);
      throw error;
    }
  },

  /**
   * Get verification statistics
   */
  async getVerificationStatistics() {
    try {
      // Pending payments
      const { data: pendingPayments, error: paymentsError } = await supabase
        ?.from('payment_transactions')
        ?.select('id, amount')
        ?.in('payment_method', ['bsp', 'kinabank', 'creditbank'])
        ?.eq('reconciliation_status', 'pending');

      if (paymentsError) throw paymentsError;

      // Pending activations
      const { data: pendingActivations, error: activationsError } = await supabase
        ?.from('company_subscriptions')
        ?.select('id')
        ?.in('subscription_status', ['suspended', 'cancelled']);

      if (activationsError) throw activationsError;

      // Today's verifications
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data: todayVerifications, error: todayError } = await supabase
        ?.from('payment_transactions')
        ?.select('id')
        ?.eq('reconciliation_status', 'reconciled')
        ?.gte('reconciled_at', today);

      if (todayError) throw todayError;

      // Calculate total pending amount
      const totalPendingAmount = pendingPayments?.reduce((sum, p) => sum + (p?.amount || 0), 0) || 0;

      return {
        pendingPayments: pendingPayments?.length || 0,
        pendingActivations: pendingActivations?.length || 0,
        todayVerifications: todayVerifications?.length || 0,
        totalPendingAmountKina: totalPendingAmount
      };
    } catch (error) {
      console.error('Error fetching verification statistics:', error);
      throw error;
    }
  },

  /**
   * Search payments by criteria with real-time matching
   */
  async searchPayments(criteria) {
    try {
      let query = supabase
        ?.from('payment_transactions')
        ?.select(`
          *,
          company_settings!payment_transactions_company_id_fkey (
            id,
            company_name,
            contact_email,
            contact_phone
          )
        `)
        ?.in('payment_method', ['bsp', 'kinabank', 'creditbank'])
        ?.eq('reconciliation_status', 'pending');

      // Apply filters
      if (criteria?.amount) {
        query = query?.eq('amount', parseFloat(criteria?.amount));
      }

      if (criteria?.reference) {
        query = query?.or(`transaction_reference.ilike.%${criteria?.reference}%,receipt_number.ilike.%${criteria?.reference}%`);
      }

      if (criteria?.dateFrom) {
        query = query?.gte('created_at', criteria?.dateFrom);
      }

      if (criteria?.dateTo) {
        query = query?.lte('created_at', criteria?.dateTo);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(payment => ({
        id: payment?.id,
        companyId: payment?.company_id,
        companyName: payment?.company_settings?.company_name,
        contactEmail: payment?.company_settings?.contact_email,
        contactPhone: payment?.company_settings?.contact_phone,
        amount: payment?.amount,
        currency: payment?.currency,
        paymentMethod: payment?.payment_method,
        transactionReference: payment?.transaction_reference,
        receiptNumber: payment?.receipt_number,
        reconciliationStatus: payment?.reconciliation_status,
        status: payment?.status,
        createdAt: payment?.created_at,
        updatedAt: payment?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error searching payments:', error);
      throw error;
    }
  },

  /**
   * Get recent verification activity
   */
  async getRecentVerificationActivity(limit = 20) {
    try {
      const { data, error } = await supabase
        ?.from('workforce_audit_logs')
        ?.select('*')
        ?.eq('entity_type', 'payment_transaction')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        actionType: log?.action_type,
        performedBy: log?.performed_by,
        changes: log?.changes,
        createdAt: log?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching verification activity:', error);
      throw error;
    }
  }
};

export default manualPaymentVerificationService;